import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { Icon } from './memberIcons';
import m from './Member.module.scss';

/**
 * [2026-09-13 사용자] 계정 정보·설정 패널 — 프로필 + 비밀번호 변경/설정 + 마케팅 수신 + 로그아웃/탈퇴.
 *   ★관리자(AdminHome '내 정보' 탭)와 사용자(MyPage '사용자 정보' 탭)가 공용 재사용.
 *   기존 MyPage의 '내 활동' 바로가기는 탭으로 분리됐으므로 여기엔 없음.
 */
function pwOk(pw: string) {
	if (pw.length < 8 || pw.length > 20) return false;
	let l = false, d = false, sp = false;
	for (const c of pw) { if (/[a-zA-Z]/.test(c)) l = true; else if (/[0-9]/.test(c)) d = true; else sp = true; }
	return l && d && sp;
}
const retLabel = (v: number | null | undefined) => (!v ? '회원 탈퇴 시까지' : v % 12 === 0 ? `${v / 12}년` : `${v}개월`);

function AccountPanel() {
	const auth = useAuth();
	const nav = useNavigate();
	const [cur, setCur] = useState('');
	const [nw, setNw] = useState('');
	const [nw2, setNw2] = useState('');
	const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
	const [busy, setBusy] = useState(false);
	const [mk, setMk] = useState({ sms: false, email: false });
	const [mkMsg, setMkMsg] = useState<string | null>(null);
	const [withdrawOpen, setWithdrawOpen] = useState(false);
	const [withdrawDone, setWithdrawDone] = useState(false);

	useEffect(() => {
		if (!auth.isAuthed) return;
		authApi.getMarketing().then((r) => { if (r.success) setMk({ sms: !!r.smsOpt, email: !!r.emailOpt }); });
	}, [auth.isAuthed]);

	const u = auth.user;
	if (!u) return <div />;

	async function changePw(e: React.FormEvent) {
		e.preventDefault(); setMsg(null);
		if (!pwOk(nw)) return setMsg({ ok: false, text: '새 비밀번호는 영문·숫자·특수문자를 포함한 8~20자여야 합니다.' });
		if (nw !== nw2) return setMsg({ ok: false, text: '새 비밀번호가 일치하지 않습니다.' });
		setBusy(true);
		const r = await authApi.changePassword(cur, nw);
		setBusy(false);
		setMsg({ ok: !!r.success, text: r.message || (r.success ? '변경되었습니다.' : '변경에 실패했습니다.') });
		if (r.success) { setCur(''); setNw(''); setNw2(''); }
	}
	async function setPw(e: React.FormEvent) {
		e.preventDefault(); setMsg(null);
		if (!pwOk(nw)) return setMsg({ ok: false, text: '비밀번호는 영문·숫자·특수문자를 포함한 8~20자여야 합니다.' });
		if (nw !== nw2) return setMsg({ ok: false, text: '비밀번호가 일치하지 않습니다.' });
		setBusy(true);
		const r = await authApi.setPassword(nw);
		setBusy(false);
		setMsg({ ok: !!r.success, text: r.message || (r.success ? '설정되었습니다.' : '설정에 실패했습니다.') });
		if (r.success) { setNw(''); setNw2(''); await auth.restore(); }
	}
	async function logout() { await auth.logout(); nav('/', { replace: true }); }
	async function saveMarketing(next: { sms: boolean; email: boolean }) {
		setMk(next);
		const r = await authApi.setMarketing(next.sms, next.email);
		setMkMsg(r.success ? '수신설정이 저장되었습니다.' : (r.message || '저장에 실패했습니다.'));
	}
	async function doWithdraw(pw?: string) {
		if (u && !u.socialProvider && !(pw && pw.trim())) { setMsg({ ok: false, text: '현재 비밀번호를 입력하세요.' }); return; }
		const r = await authApi.withdraw(pw);
		setWithdrawOpen(false);
		if (!r.success) { setMsg({ ok: false, text: r.message || '탈퇴에 실패했습니다.' }); return; }
		setWithdrawDone(true);
	}

	return (
		<>
			<ConfirmModal open={withdrawOpen} danger title="회원 탈퇴" confirmText="탈퇴하기"
				message={`정말 탈퇴하시겠습니까?\n계정과 정보가 삭제되며 되돌릴 수 없습니다.${u.socialProvider ? '' : '\n\n확인을 위해 현재 비밀번호를 입력하세요.'}`}
				inputType={u.socialProvider ? undefined : 'password'} inputPlaceholder="현재 비밀번호"
				onConfirm={(v) => doWithdraw(v)} onCancel={() => setWithdrawOpen(false)} />
			<ConfirmModal open={withdrawDone} alertMode title="탈퇴 완료"
				message="탈퇴가 완료되었습니다. 이용해 주셔서 감사합니다." onConfirm={() => { auth.clearSession(); nav('/', { replace: true }); }} onCancel={() => {}} />

			<div className={m.mpGrid}>
				<div>
					<div className={m.card}>
						<div className={m.profile}>
							<div className={m.avatar}>{u.name.charAt(0)}</div>
							<div className={m.pName}>{u.name}
								<span className={m.roleTag}>{u.role === 'ADMIN' ? '최고관리자' : '일반회원'}</span></div>
						</div>
						<div className={m.infoRow}><span className="k">아이디</span><span className="v">{u.username || (u.socialProvider ? `${u.socialProvider} 소셜` : u.email)}</span></div>
						<div className={m.infoRow}><span className="k">휴대폰</span><span className="v">{u.phone || '-'}</span></div>
						<div className={m.infoRow}><span className="k">이메일</span><span className="v">{u.email || '-'}</span></div>
						<div className={m.infoRow}><span className="k">개인정보 유효기간</span>
							<span className="v"><span className={m.retView}><Icon.calendar />{retLabel(u.retentionMonths)}</span></span></div>
					</div>
				</div>

				<div>
					<div className={m.card}>
						<div className={m.cardTitle}><Icon.key />{u.hasPassword ? '비밀번호 변경' : '비밀번호 설정'}</div>
						{msg && <div className={`${m.msg} ${msg.ok ? m.msgOk : m.msgErr}`}>{msg.text}</div>}
						{u.hasPassword ? (
							<form onSubmit={changePw}>
								<div className={m.field}><input className={m.input} style={{ width: '100%' }} type="password" placeholder="현재 비밀번호"
									value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" /></div>
								<div className={m.field}><input className={m.input} style={{ width: '100%' }} type="password" placeholder="새 비밀번호 (영문·숫자·특수문자 8~20자)"
									value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" /></div>
								<div className={m.field}><input className={m.input} style={{ width: '100%' }} type="password" placeholder="새 비밀번호 확인"
									value={nw2} onChange={(e) => setNw2(e.target.value)} autoComplete="new-password" /></div>
								<button className={m.pill} disabled={busy || !cur || !nw}>{busy ? '변경 중…' : '비밀번호 변경'}</button>
							</form>
						) : (
							<>
								<p className={m.hint} style={{ marginBottom: 12 }}>
									{u.socialProvider
										? `${u.socialProvider} 소셜 로그인 계정입니다. 비밀번호를 설정하면 이메일(${u.email || '가입 이메일'})과 비밀번호로도 로그인할 수 있습니다.`
										: '비밀번호를 설정해주세요.'}
								</p>
								<form onSubmit={setPw}>
									<div className={m.field}><input className={m.input} style={{ width: '100%' }} type="password" placeholder="새 비밀번호 (영문·숫자·특수문자 8~20자)"
										value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password" /></div>
									<div className={m.field}><input className={m.input} style={{ width: '100%' }} type="password" placeholder="새 비밀번호 확인"
										value={nw2} onChange={(e) => setNw2(e.target.value)} autoComplete="new-password" /></div>
									<button className={m.pill} disabled={busy || !nw}>{busy ? '설정 중…' : '비밀번호 설정'}</button>
								</form>
							</>
						)}
					</div>

					<div className={m.card}>
						<div className={m.cardTitle}><Icon.mail />마케팅 수신설정</div>
						{mkMsg && <p className={`${m.hint} ${m.hintOk}`}>{mkMsg}</p>}
						<label className={m.check}><input type="checkbox" checked={mk.sms} onChange={(e) => saveMarketing({ ...mk, sms: e.target.checked })} /> SMS 수신 동의</label>
						<label className={m.check}><input type="checkbox" checked={mk.email} onChange={(e) => saveMarketing({ ...mk, email: e.target.checked })} /> 이메일 수신 동의</label>
					</div>

					<div className={m.card}>
						<div className={m.cardTitle}><Icon.logout />계정</div>
						{u.role === 'ADMIN' && (
							<button className={m.ghost} style={{ marginBottom: 10 }} onClick={() => nav('/admin')}>관리자 페이지로 이동</button>
						)}
						<button className={m.ghost} onClick={logout}>로그아웃</button>
						{u.role !== 'ADMIN' && (
							<button className={`${m.ghost} ${m.danger}`} style={{ marginTop: 10 }} onClick={() => setWithdrawOpen(true)}>회원 탈퇴</button>
						)}
					</div>
				</div>
			</div>
		</>
	);
}

export default observer(AccountPanel);
